import React, { useEffect, useState } from 'react';
import { getConfig } from '../config';

type Row = {
  workspaceName: string;
  recordingCount: number;
  totalHours: number;
  ec2StreamCost: number;
  gpuCost: number;
  s3StorageCost: number;
  internetTransferCost: number;
  regionalTransferCost: number;
  fixedCosts: number;
  totalEstimatedCost: number;
  totalVariableCostPerHour?: number;
};

type Workspace = { id: number; name: string };

const AnalyticsCostAttribution: React.FC = () => {
  const { apiBase } = getConfig();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [rows, setRows] = useState<Row[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [month, setMonth] = useState<string>(defaultMonth);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sort, setSort] = useState<'workspaceName' | 'recordingCount' | 'totalHours' | 'totalEstimatedCost' | 'ec2' | 'gpu' | 's3' | 'internet' | 'regional' | 'fixed' | 'varPerHour' | 'ec2PerHour' | 'gpuPerHour' | 's3PerHour' | 'internetPerHour' | 'regionalPerHour'>('totalEstimatedCost');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [granularity, setGranularity] = useState<'monthly' | 'hourly'>('monthly');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/workspaces/options`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to load workspaces: ${res.status}`);
        const data = (await res.json()) as Workspace[];
        setWorkspaces(data);
      } catch (e: any) {
        console.warn('Workspace list load failed:', e?.message || e);
      }
    })();
  }, [apiBase]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const url = new URL(`${apiBase}/superadmin/analytics/cost-attribution`);
        if (month) url.searchParams.set('month', month);
        if (workspaceId) url.searchParams.set('workspaceId', workspaceId);
        if (sort) url.searchParams.set('sort', sort);
        if (order) url.searchParams.set('order', order);
        const res = await fetch(url.toString(), { credentials: 'include' });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = (await res.json()) as Row[];
        setRows(data);
      } catch (e: any) {
        setErr(e?.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, month, workspaceId, sort, order]);

  if (loading) return <div>Loading…</div>;
  if (err) return <div className="text-red-600">Error: {err}</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Cost Attribution per Workspace (Monthly)</h1>
      <div className="text-sm text-gray-700 mb-4">Note: AWS costs are based on documented estimates and may not exactly match actual billing.</div>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm">Month</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border rounded px-2 py-1 text-sm" />
        <label className="text-sm">Workspace</label>
        <select value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} className="border rounded px-2 py-1 text-sm">
          <option value="">All</option>
          {workspaces.map(w => (<option key={w.id} value={w.id}>{w.name}</option>))}
        </select>
        <label className="text-sm">View</label>
        <select value={granularity} onChange={(e) => setGranularity(e.target.value as any)} className="border rounded px-2 py-1 text-sm">
          <option value="monthly">Monthly</option>
          <option value="hourly">Hourly</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('workspaceName'); setOrder(o => sort==='workspaceName'?(o==='asc'?'desc':'asc'):'asc'); }}>Workspace</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('recordingCount'); setOrder(o => sort==='recordingCount'?(o==='asc'?'desc':'asc'):'desc'); }}>Recordings</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('totalHours'); setOrder(o => sort==='totalHours'?(o==='asc'?'desc':'asc'):'desc'); }}>Total Hours</th>
              {granularity === 'monthly' ? (
                <>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('ec2'); setOrder(o => sort==='ec2'?(o==='asc'?'desc':'asc'):'desc'); }}>EC2</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('gpu'); setOrder(o => sort==='gpu'?(o==='asc'?'desc':'asc'):'desc'); }}>GPU</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('s3'); setOrder(o => sort==='s3'?(o==='asc'?'desc':'asc'):'desc'); }}>S3</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('internet'); setOrder(o => sort==='internet'?(o==='asc'?'desc':'asc'):'desc'); }}>Internet</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('regional'); setOrder(o => sort==='regional'?(o==='asc'?'desc':'asc'):'desc'); }}>Regional</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('fixed'); setOrder(o => sort==='fixed'?(o==='asc'?'desc':'asc'):'desc'); }}>Fixed</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('totalEstimatedCost'); setOrder(o => sort==='totalEstimatedCost'?(o==='asc'?'desc':'asc'):'desc'); }}>Total</th>
                </>
              ) : (
                <>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('ec2PerHour'); setOrder(o => sort==='ec2PerHour'?(o==='asc'?'desc':'asc'):'desc'); }}>EC2 $/hr</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('gpuPerHour'); setOrder(o => sort==='gpuPerHour'?(o==='asc'?'desc':'asc'):'desc'); }}>GPU $/hr</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('s3PerHour'); setOrder(o => sort==='s3PerHour'?(o==='asc'?'desc':'asc'):'desc'); }}>S3 $/hr</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('internetPerHour'); setOrder(o => sort==='internetPerHour'?(o==='asc'?'desc':'asc'):'desc'); }}>Internet $/hr</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('regionalPerHour'); setOrder(o => sort==='regionalPerHour'?(o==='asc'?'desc':'asc'):'desc'); }}>Regional $/hr</th>
                  <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('varPerHour'); setOrder(o => sort==='varPerHour'?(o==='asc'?'desc':'asc'):'desc'); }}>Total Var $/hr</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={`${r.workspaceName}-${idx}`} className="border-b last:border-none">
                <td className="p-3">{r.workspaceName}</td>
                <td className="p-3">{r.recordingCount}</td>
                <td className="p-3">{r.totalHours}</td>
                {granularity === 'monthly' ? (
                  <>
                    <td className="p-3">${r.ec2StreamCost.toFixed(2)}</td>
                    <td className="p-3">${r.gpuCost.toFixed(2)}</td>
                    <td className="p-3">${r.s3StorageCost.toFixed(2)}</td>
                    <td className="p-3">${r.internetTransferCost.toFixed(2)}</td>
                    <td className="p-3">${r.regionalTransferCost.toFixed(2)}</td>
                    <td className="p-3">${r.fixedCosts.toFixed(2)}</td>
                    <td className="p-3 font-semibold">${r.totalEstimatedCost.toFixed(2)}</td>
                  </>
                ) : (
                  <>
                    <td className="p-3">${(r.ec2StreamCost / (r.totalHours || 1)).toFixed(2)}</td>
                    <td className="p-3">${(r.gpuCost / (r.totalHours || 1)).toFixed(2)}</td>
                    <td className="p-3">${(r.s3StorageCost / (r.totalHours || 1)).toFixed(2)}</td>
                    <td className="p-3">${(r.internetTransferCost / (r.totalHours || 1)).toFixed(2)}</td>
                    <td className="p-3">${(r.regionalTransferCost / (r.totalHours || 1)).toFixed(2)}</td>
                    <td className="p-3 font-semibold">${((r.ec2StreamCost + r.gpuCost + r.s3StorageCost + r.internetTransferCost + r.regionalTransferCost) / (r.totalHours || 1)).toFixed(2)}</td>
                  </>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td className="p-3" colSpan={granularity === 'monthly' ? 10 : 9}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnalyticsCostAttribution;


